import { Box, styled } from "@material-ui/core";
import { memo, useEffect, useState } from "react";
import {
  EPOCUS_ORANGE,
  EPOCUS_SYNCED_BG,
  EPOCUS_SYNCED_TEXT,
  EPOCUS_TO_DOC_BG,
  EPOCUS_TO_DOC_BORDER,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type { IconName } from "../onboarding-chat";
import { CheckIcon, Icon, isKnownIcon } from "./icons";

const REVEAL_INTERVAL_MS = 650;

const Card = styled(Box)({
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  backgroundColor: EPOCUS_TO_DOC_BG,
  borderRadius: 12,
  overflow: "hidden",
  margin: "4px 0",
});

const Header = styled(Box)({
  padding: "11px 14px",
  fontSize: rem(12),
  fontWeight: 600,
  color: EPOCUS_ORANGE,
  display: "flex",
  alignItems: "center",
  gap: 8,
  borderBottom: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
});

const Step = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "11px 14px",
  fontSize: rem(13),
  borderTop: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  transition: "opacity .35s, transform .35s",
  "&:first-child": { borderTop: "none" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

const StepIcon = styled("span")({
  width: 24,
  height: 24,
  borderRadius: 7,
  flex: "none",
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: EPOCUS_ORANGE,
});

const Amount = styled("span")({
  marginLeft: "auto",
  fontWeight: 700,
  color: EPOCUS_SYNCED_TEXT,
  fontSize: rem(14),
});

const Footer = styled(Box)({
  padding: "11px 14px",
  backgroundColor: EPOCUS_SYNCED_BG,
  borderTop: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  fontSize: rem(12),
  color: EPOCUS_SYNCED_TEXT,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "opacity .4s",
});

const HIDDEN = { opacity: 0, transform: "translateX(-6px)" } as const;
const SHOWN = { opacity: 1, transform: "none" } as const;

export const PayoffCard = memo<{
  title: string;
  steps: { label: string; icon?: IconName; amount?: string }[];
  footer?: string;
}>(({ title, steps, footer }) => {
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (revealed > steps.length) return;
    const timer = setTimeout(
      () => setRevealed((current) => current + 1),
      REVEAL_INTERVAL_MS,
    );
    return () => clearTimeout(timer);
  }, [revealed, steps.length]);

  return (
    <Card>
      <Header>
        <Icon name="dollar" size={14} />
        {title}
      </Header>
      {steps.map((step, index) => (
        <Step key={step.label} style={index < revealed ? SHOWN : HIDDEN}>
          <StepIcon>
            <Icon name={isKnownIcon(step.icon) ? step.icon : "doc"} size={13} />
          </StepIcon>
          <span>{step.label}</span>
          {step.amount && <Amount>{step.amount}</Amount>}
        </Step>
      ))}
      {footer && (
        <Footer style={{ opacity: revealed > steps.length ? 1 : 0 }}>
          <CheckIcon size={14} />
          {footer}
        </Footer>
      )}
    </Card>
  );
});
PayoffCard.displayName = "PayoffCard";
